import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import planService from '../../services/planService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const PlansDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await planService.getPlanStats();
      setStats(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching plan statistics:', error);
      setError('Failed to fetch plan statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  const StatusCard = ({ title, value, icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4">
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading plan statistics...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={fetchStats} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No statistics available</Typography>
      </Box>
    );
  }

  // Prepare chart data
  const durationChartData = {
    labels: stats.durationStats?.map(item => `${item._id} days`) || [],
    datasets: [
      {
        label: 'Number of Plans',
        data: stats.durationStats?.map(item => item.count) || [],
        backgroundColor: [
          'rgba(30, 58, 138, 0.75)',
          'rgba(37, 99, 235, 0.7)',
          'rgba(45, 212, 191, 0.7)',
          'rgba(21, 128, 61, 0.65)',
          'rgba(180, 83, 9, 0.65)',
          'rgba(71, 85, 105, 0.7)',
        ],
        borderColor: [
          'rgba(30, 58, 138, 1)',
          'rgba(37, 99, 235, 1)',
          'rgba(45, 212, 191, 1)',
          'rgba(21, 128, 61, 1)',
          'rgba(180, 83, 9, 1)',
          'rgba(71, 85, 105, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const statusChartData = {
    labels: ['Active Plans', 'Inactive Plans'],
    datasets: [
      {
        data: [stats.activePlans, stats.inactivePlans],
        backgroundColor: [
          'rgba(21, 128, 61, 0.85)',
          'rgba(185, 28, 28, 0.8)',
        ],
        borderColor: [
          'rgba(21, 128, 61, 1)',
          'rgba(185, 28, 28, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Plans by Duration',
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Plan Status Distribution',
      },
    },
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h4" gutterBottom>
        Plans Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Track plan performance, pricing distribution, and active coverage.
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatusCard
            title="Total Plans"
            value={stats.totalPlans}
            icon={<TrendingUpIcon fontSize="large" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatusCard
            title="Active Plans"
            value={stats.activePlans}
            icon={<ActiveIcon fontSize="large" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatusCard
            title="Inactive Plans"
            value={stats.inactivePlans}
            icon={<InactiveIcon fontSize="large" />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatusCard
            title="Avg Price"
            value={formatPrice(stats.priceStats?.avgPrice || 0)}
            icon={<MoneyIcon fontSize="large" />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Price Information */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Price Range
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Minimum Price
                  </Typography>
                  <Typography variant="h6">
                    {formatPrice(stats.priceStats?.minPrice || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Maximum Price
                  </Typography>
                  <Typography variant="h6">
                    {formatPrice(stats.priceStats?.maxPrice || 0)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Duration Information */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Durations
              </Typography>
              <Grid container spacing={1}>
                {stats.durationStats?.map((item, index) => (
                  <Grid item key={index}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        p: 1,
                        textAlign: 'center',
                        minWidth: 80
                      }}
                    >
                      <Typography variant="body2" color="primary">
                        {item._id} days
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.count} plan{item.count !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Bar data={durationChartData} options={chartOptions} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Doughnut data={statusChartData} options={doughnutOptions} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlansDashboard;
